import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    useSessionActivityReport,
    useExportSessionActivity,
    type SessionActivityFilter,
} from '@/api/reports';
import { useClientNames } from '@/api/clients';
import { useUserDirectory } from '@/api/users';
import { useCancelReasons } from '@/api/cancelReasons';
import { useSessionTypes } from '@/api/sessionTypes';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/FormField';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
const STATUSES: Array<SessionActivityFilter['status'] & string> = ['Scheduled', 'Complete', 'Finalized', 'Canceled'];

const NOTE_STATUSES: Array<{ value: NonNullable<SessionActivityFilter['noteStatus']>; label: string }> = [
    { value: 'Missing',   label: 'Missing (completed, no note)' },
    { value: 'Drafting',  label: 'Drafting' },
    { value: 'Submitted', label: 'Submitted' },
];

export function SessionActivityPage() {
    const canExport = useAuthStore((s) => s.hasPermission('export_reports'));

    const [filter, setFilter] = useState<SessionActivityFilter>({});
    const [draft, setDraft] = useState<SessionActivityFilter>({});

    const { data, isLoading, isError, error, isFetching } = useSessionActivityReport(filter);
    const exportM = useExportSessionActivity();
    const clientsQuery = useClientNames();
    const staffQuery   = useUserDirectory();
    const reasonsQuery = useCancelReasons();
    const sessionTypesQuery = useSessionTypes();

    const clientOptions = useMemo(() => {
        if (!clientsQuery.data) return [];
        return clientsQuery.data
            .map((c) => ({ id: c.id, label: `${c.last}, ${c.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [clientsQuery.data]);

    const staffOptions = useMemo(() => {
        if (!staffQuery.data) return [];
        return staffQuery.data
            .map((s) => ({ id: s.id, label: `${s.last}, ${s.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [staffQuery.data]);

    const reasonOptions = useMemo(() => {
        if (!reasonsQuery.data) return [];
        return reasonsQuery.data
            .filter((r) => r.active)
            .map((r) => ({ id: r.id, label: r.text }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [reasonsQuery.data]);

    const typeOptions = useMemo(() => {
        if (!sessionTypesQuery.data) return [];
        return sessionTypesQuery.data
            .filter((t) => t.status === 'Active')
            .map((t) => ({ id: t.id, label: t.name }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [sessionTypesQuery.data]);

    function apply() { setFilter({ ...draft }); }
    function clear() { setDraft({}); setFilter({}); }

    async function runExport() {
        try { await exportM.mutateAsync(filter); } catch { /* isError renders below */ }
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                        <ArrowLeft className="w-3 h-3" /> All reports
                    </Link>
                    <h1 className="font-display text-2xl font-bold text-text">Session Activity</h1>
                    <p className="text-xs text-text-subtle">
                        Scheduled vs. completed vs. canceled, with per-staff and per-client rollups and a missing-notes filter.
                    </p>
                </div>
                {canExport && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={runExport}
                        disabled={exportM.isPending || isLoading}
                    >
                        {exportM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Export CSV
                    </Button>
                )}
            </div>

            <Card>
                <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <SelectField label="Client" value={draft.clientId} options={clientOptions}
                            onChange={(v) => setDraft({ ...draft, clientId: v })} />
                        <SelectField label="Staff" value={draft.staffId} options={staffOptions}
                            onChange={(v) => setDraft({ ...draft, staffId: v })} />
                        <SelectField label="Session type" value={draft.sessionTypeId} options={typeOptions}
                            onChange={(v) => setDraft({ ...draft, sessionTypeId: v })} />
                        <SelectField label="Cancel reason" value={draft.cancelReasonId} options={reasonOptions}
                            onChange={(v) => setDraft({ ...draft, cancelReasonId: v })} />

                        <label className="text-xs block">
                            <span className="text-text-muted">Status</span>
                            <select
                                value={draft.status ?? ''}
                                onChange={(e) => setDraft({ ...draft, status: (e.target.value || undefined) as SessionActivityFilter['status'] })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All statuses</option>
                                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Note status</span>
                            <select
                                value={draft.noteStatus ?? ''}
                                onChange={(e) => setDraft({ ...draft, noteStatus: (e.target.value || undefined) as SessionActivityFilter['noteStatus'] })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All notes</option>
                                {NOTE_STATUSES.map((n) => <option key={n.value} value={n.value}>{n.label}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Date from</span>
                            <Input
                                type="date"
                                min="1900-01-01"
                                max={toIsoDate(new Date())}
                                value={draft.dateFrom ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Date to</span>
                            <Input
                                type="date"
                                min={draft.dateFrom || '1900-01-01'}
                                max={toIsoDate(new Date())}
                                value={draft.dateTo ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <Button variant="primary" size="sm" onClick={apply}>Apply</Button>
                        <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
                        {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-subtle" />}
                    </div>
                </CardBody>
            </Card>

            {isError ? (
                <Card>
                    <CardBody>
                        <p className="text-sm text-error font-medium">Couldn't load report</p>
                        <p className="text-xs text-text-subtle mt-1">{extractErrorMessage(error, 'Please try again.')}</p>
                    </CardBody>
                </Card>
            ) : isLoading || !data ? (
                <Card><CardBody><div className="flex items-center gap-2 text-text-subtle text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></CardBody></Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <Kpi label="Total" value={data.kpis.total.toLocaleString()} />
                        <Kpi label="Completed" value={data.kpis.completed.toLocaleString()} />
                        <Kpi label="Canceled" value={data.kpis.canceled.toLocaleString()} />
                        <Kpi label="Scheduled" value={data.kpis.scheduled.toLocaleString()} />
                        <Kpi label="Notes missing" value={data.kpis.notesMissing.toLocaleString()} emphasis={data.kpis.notesMissing > 0} />
                    </div>

                    <Card>
                        <CardHeader><CardTitle>By staff</CardTitle></CardHeader>
                        <CardBody className="pt-0">
                            {data.byStaff.length === 0 ? (
                                <p className="text-xs text-text-subtle px-3 py-4">No sessions match the current filter.</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Staff</th>
                                            <th className="px-3 py-2 font-bold text-right">Total</th>
                                            <th className="px-3 py-2 font-bold text-right">Completed</th>
                                            <th className="px-3 py-2 font-bold text-right">Canceled</th>
                                            <th className="px-3 py-2 font-bold text-right">Notes missing</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.byStaff.map((s) => (
                                            <tr key={s.staffId ?? s.staffName} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2">{s.staffName}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{s.total}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{s.completed}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{s.canceled}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    {s.notesMissing > 0
                                                        ? <span className="text-warning-text font-medium">{s.notesMissing}</span>
                                                        : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader><CardTitle>Cancellations by reason</CardTitle></CardHeader>
                            <CardBody className="pt-0">
                                {data.byCancelReason.length === 0 ? (
                                    <p className="text-xs text-text-subtle px-3 py-4">No cancellations in this period.</p>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                                <th className="px-3 py-2 font-bold">Reason</th>
                                                <th className="px-3 py-2 font-bold text-right">Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.byCancelReason.map((r) => (
                                                <tr key={r.cancelReasonId ?? r.label} className="border-b border-border last:border-b-0">
                                                    <td className="px-3 py-2">{r.label}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </CardBody>
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>By client</CardTitle></CardHeader>
                            <CardBody className="pt-0">
                                {data.byClient.length === 0 ? (
                                    <p className="text-xs text-text-subtle px-3 py-4">No sessions match the current filter.</p>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                                <th className="px-3 py-2 font-bold">Client</th>
                                                <th className="px-3 py-2 font-bold text-right">Total</th>
                                                <th className="px-3 py-2 font-bold text-right">Completed</th>
                                                <th className="px-3 py-2 font-bold text-right">Canceled</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.byClient.map((c) => (
                                                <tr key={c.clientId} className="border-b border-border last:border-b-0">
                                                    <td className="px-3 py-2">
                                                        <Link to={`/clients/${c.clientId}`} className="hover:underline">{c.clientName}</Link>
                                                    </td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{c.total}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{c.completed}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{c.canceled}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </>
            )}
        </div>
    );
}

function SelectField({
    label, value, options, onChange,
}: {
    label: string;
    value: string | undefined;
    options: { id: string; label: string }[];
    onChange: (v: string | undefined) => void;
}) {
    return (
        <label className="text-xs block">
            <span className="text-text-muted">{label}</span>
            <select
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value || undefined)}
                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
            >
                <option value="">All</option>
                {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
        </label>
    );
}

function Kpi({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
    return (
        <Card>
            <CardBody>
                <p className="text-2xs uppercase tracking-wider text-text-subtle">{label}</p>
                <p className={`font-display text-xl font-bold mt-1 tabular-nums ${emphasis ? 'text-warning-text' : 'text-text'}`}>{value}</p>
            </CardBody>
        </Card>
    );
}
